import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
    ArrowLeft, Plus, Trash2, ChevronUp, ChevronDown, Loader2, Save, Rocket,
    Settings, Palette, Globe, Eye, ExternalLink, GripVertical, GitBranch, Image as ImageIcon, Code, CheckCircle,
} from 'lucide-react';
import FlowScreen from '../components/flow/FlowScreen';
import SplashScreen from '../components/flow/SplashScreen';
import EndPageRenderer from '../components/flow/EndPageRenderer';
import ConditionBuilder from '../components/workflow/ConditionBuilder';
import CustomDomainPanel from '../components/sites/CustomDomainPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUESTION_TYPES = [
    ['single_select', 'Single select'], ['multi_select', 'Multi-select'], ['dropdown', 'Dropdown'],
    ['yes_no', 'Yes / No'], ['short_text', 'Short text'], ['long_text', 'Long text'],
    ['number', 'Number'], ['monetary', 'Monetary'], ['rating', 'Rating / scale'],
    ['email', 'Email'], ['phone', 'Phone'],
];
const HAS_OPTIONS = new Set(['single_select', 'multi_select', 'dropdown']);
const CONTACT_FIELDS = ['first_name', 'last_name', 'email', 'phone', 'mobile', 'title', 'department', 'seniority', 'linkedin_url'];
const ACCOUNT_FIELDS = ['company_name', 'company_website', 'industry', 'company_size', 'company_phone', 'company_email', 'company_city', 'company_country', 'company_linkedin_url'];
const Q_TO_CF = { single_select: 'select', dropdown: 'select', multi_select: 'multi_select', yes_no: 'checkbox', short_text: 'text', long_text: 'textarea', number: 'number', monetary: 'number', rating: 'number', email: 'email', phone: 'phone' };

const inp = "w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/15";
const lbl = "block text-xs font-medium text-stone-600 mb-1";

const rid = (p) => `${p}_${Math.random().toString(36).slice(2, 10)}`;
const newQuestionScreen = () => ({ step_id: rid('s'), kind: 'question', branch: null, question_id: rid('q'), type: 'short_text', label: '', help_text: '', required: false, options: [], placeholder: '', map_to: null, config: {} });
const newSplashScreen = () => ({ step_id: rid('s'), kind: 'splash', branch: null, splash: { headline: '', body: '', image_url: '', testimonial: null, button_label: 'Continue' } });
const newEndPageScreen = () => ({ step_id: rid('s'), kind: 'end_page', branch: null, end_page: { headline: '', subheadline: '', blocks: [], enable_coach_assignment: false, enable_booking: false } });

// End-page block types + default props (Batch 3).
const END_BLOCK_TYPES = [
    ['headline', 'Headline'], ['subheadline', 'Subheadline'], ['body', 'Body text'],
    ['video', 'Video'], ['iframe', 'Embed (iframe)'], ['cta', 'CTA button'],
    ['coach_card', 'Coach card'], ['booking', 'Booking'],
];
const endBlockDefaults = (type) => {
    const base = { block_id: rid('b'), type, order: 0, enabled: true, props: {} };
    if (type === 'cta') base.props = { label: 'Continue', url: '' };
    if (type === 'coach_card') base.props = { show_bio: true, show_phone: true, show_linkedin: true };
    if (type === 'iframe') base.props = { url: '', height: 480 };
    return base;
};
const endBlockLabel = (type) => (END_BLOCK_TYPES.find(([v]) => v === type) || [type, type])[1];

// Map a question type → the ConditionBuilder field type (for prior-answer logic).
const condType = (t) => (t === 'number' || t === 'monetary' || t === 'rating') ? 'number' : (t === 'multi_select' ? 'array' : 'string');
const condOptions = (sc) => {
    if (sc.type === 'yes_no') return [{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }];
    if (HAS_OPTIONS.has(sc.type)) return (sc.options || []).map(o => (typeof o === 'string' ? { value: o, label: o } : o));
    return undefined;
};
const screenLabel = (sc) => {
    if (sc.kind === 'splash') return `Splash: ${sc.splash?.headline || '(untitled)'}`;
    if (sc.kind === 'end_page') return `End page: ${sc.end_page?.headline || '(untitled)'}`;
    return sc.label || 'Question';
};

export default function FormFlowBuilder() {
    const { flowId } = useParams();
    const navigate = useNavigate();
    const { getAuthHeader } = useAuth();

    const [flow, setFlow] = useState(null);
    const [name, setName] = useState('');
    const [screens, setScreens] = useState([]);
    const [settings, setSettings] = useState({});
    const [branding, setBranding] = useState({});
    const [defaults, setDefaults] = useState({});
    const [customFields, setCustomFields] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dialog, setDialog] = useState(null);
    const [preview, setPreview] = useState(false);
    const [newField, setNewField] = useState(null);

    const load = useCallback(async () => {
        try {
            const [fRes, cfRes] = await Promise.all([
                axios.get(`${API}/form-flows/${flowId}`, { headers: getAuthHeader() }),
                axios.get(`${API}/admin/custom-fields?entity=contact`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
            ]);
            const f = fRes.data;
            setFlow(f);
            setName(f.name || '');
            // steps → flat screens (preserve step_id so branch targets stay valid)
            setScreens((f.steps || []).map(s => {
                if (s.kind === 'splash') return { step_id: s.step_id, kind: 'splash', branch: s.branch || null, splash: s.splash || { headline: '', body: '', image_url: '', testimonial: null, button_label: 'Continue' } };
                if (s.kind === 'end_page') return { step_id: s.step_id, kind: 'end_page', branch: s.branch || null, end_page: s.end_page || { headline: '', subheadline: '', blocks: [], enable_coach_assignment: false, enable_booking: false } };
                return { step_id: s.step_id, kind: 'question', branch: s.branch || null, ...(s.questions?.[0] || newQuestionScreen()) };
            }));
            setSettings(f.settings || {});
            setBranding(f.branding || {});
            setDefaults(f.default_lead_fields || {});
            setCustomFields(Array.isArray(cfRes.data) ? cfRes.data : (cfRes.data?.data || []));
        } catch { toast.error('Failed to load flow'); navigate('/form-flows'); }
        finally { setLoading(false); }
    }, [flowId, getAuthHeader, navigate]);

    useEffect(() => { load(); }, [load]);

    const patchScreen = (i, p) => setScreens(s => s.map((sc, idx) => idx === i ? { ...sc, ...p } : sc));
    const moveScreen = (i, d) => setScreens(s => { const j = i + d; if (j < 0 || j >= s.length) return s; const n = [...s]; [n[i], n[j]] = [n[j], n[i]]; return n; });
    const delScreen = (i) => setScreens(s => s.filter((_, idx) => idx !== i));
    const addQuestion = () => setScreens(s => [...s, newQuestionScreen()]);
    const addSplash = () => setScreens(s => [...s, newSplashScreen()]);
    const addEndPage = () => setScreens(s => [...s, newEndPageScreen()]);

    const questionFromScreen = (sc) => ({
        question_id: sc.question_id, type: sc.type, label: sc.label || '', help_text: sc.help_text || '',
        required: !!sc.required, options: sc.options || [], placeholder: sc.placeholder || '',
        map_to: sc.map_to || null, map_to_entity: sc.map_to_entity || null, config: sc.config || {}, order: 0,
    });

    const buildPayload = () => ({
        name,
        steps: screens.map((sc, i) => {
            if (sc.kind === 'splash') return { step_id: sc.step_id, kind: 'splash', order: i, questions: [], branch: sc.branch || null, splash: sc.splash };
            if (sc.kind === 'end_page') return { step_id: sc.step_id, kind: 'end_page', order: i, questions: [], branch: sc.branch || null, end_page: sc.end_page };
            return { step_id: sc.step_id, kind: 'question', order: i, branch: sc.branch || null, questions: [questionFromScreen(sc)] };
        }),
        settings, branding, default_lead_fields: defaults,
    });

    const save = async () => {
        setSaving(true);
        try {
            await axios.put(`${API}/form-flows/${flowId}`, buildPayload(), { headers: getAuthHeader() });
            toast.success('Saved'); await load(); return true;
        } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); return false; }
        finally { setSaving(false); }
    };
    const publish = async () => {
        if (!(await save())) return;
        try {
            const res = await axios.post(`${API}/form-flows/${flowId}/publish`, {}, { headers: getAuthHeader() });
            toast.success('Published'); setFlow(f => ({ ...f, status: 'published' }));
            if (res.data.public_url) navigator.clipboard.writeText(res.data.public_url);
        } catch { toast.error('Publish failed'); }
    };
    const unpublish = async () => {
        try { await axios.post(`${API}/form-flows/${flowId}/unpublish`, {}, { headers: getAuthHeader() }); toast.success('Unpublished'); setFlow(f => ({ ...f, status: 'draft' })); }
        catch { toast.error('Failed'); }
    };

    const createField = async () => {
        if (!newField?.label?.trim()) return;
        const sc = screens[newField.index];
        try {
            const res = await axios.post(`${API}/admin/custom-fields`,
                { label: newField.label.trim(), field_type: Q_TO_CF[sc.type] || 'text', entity: 'contact' },
                { headers: getAuthHeader() });
            const created = res.data; const key = created.key || created.field?.key;
            setCustomFields(cf => [...cf, created.field || created]);
            patchScreen(newField.index, { map_to: key });
            toast.success('Custom field created'); setNewField(null);
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create field'); }
    };

    // Logic editor inputs for screen i
    const priorFields = (i) => [{
        group: 'Previous answers',
        fields: screens.slice(0, i).filter(s => s.kind === 'question' && s.question_id)
            .map(s => ({ key: s.question_id, label: s.label || s.question_id, type: condType(s.type), options: condOptions(s) })),
    }];
    const laterTargets = (i) => [
        { value: '', label: 'Next screen' },
        ...screens.slice(i + 1).map(s => ({ value: s.step_id, label: screenLabel(s) })),
        { value: '__end__', label: 'End (submit)' },
    ];

    if (loading) return <div className="flex items-center justify-center h-screen"><Loader2 className="w-8 h-8 text-stone-400 animate-spin" /></div>;
    const publicUrl = `${window.location.origin}/flow/${flow?.slug}`;
    const embedSrc = `${window.location.origin}/flow/${flow?.slug}?embed=1`;
    const embedSnippet = flow ? `<div id="nurture-flow-${flow.slug}"></div>\n<script>\n(function(){var d=document.getElementById('nurture-flow-${flow.slug}');if(!d)return;var f=document.createElement('iframe');f.src='${embedSrc}';f.style.cssText='width:100%;border:none;max-width:640px;min-height:520px;';f.setAttribute('frameborder','0');d.appendChild(f);window.addEventListener('message',function(e){if(e.data&&e.data.type==='nurture-flow-resize'&&e.data.slug==='${flow.slug}'){f.style.height=e.data.height+'px';}});})();\n</script>` : '';

    return (
        <div className="min-h-screen bg-stone-50">
            <div className="sticky top-0 z-10 bg-white border-b border-stone-200 px-5 py-3 flex items-center gap-3">
                <button onClick={() => navigate('/form-flows')} className="text-stone-400 hover:text-stone-700"><ArrowLeft className="w-5 h-5" /></button>
                <input value={name} onChange={e => setName(e.target.value)} className="text-lg font-semibold text-stone-900 bg-transparent border border-transparent hover:border-stone-200 focus:border-stone-300 rounded px-2 py-1 focus:outline-none" style={{ fontFamily: 'Manrope, sans-serif' }} />
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${flow?.status === 'published' ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-100 text-stone-500'}`}>{flow?.status}</span>
                <div className="ml-auto flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setPreview(p => !p)}><Eye className="w-4 h-4 mr-1" /> {preview ? 'Edit' : 'Preview'}</Button>
                    <Button variant="outline" size="sm" onClick={() => setDialog('settings')}><Settings className="w-4 h-4" /></Button>
                    <Button variant="outline" size="sm" onClick={() => setDialog('branding')}><Palette className="w-4 h-4" /></Button>
                    <Button variant="outline" size="sm" onClick={() => setDialog('domain')}><Globe className="w-4 h-4" /></Button>
                    <Button variant="outline" size="sm" onClick={() => setDialog('embed')}><Code className="w-4 h-4" /></Button>
                    {flow?.status === 'published' && <Button variant="outline" size="sm" onClick={() => window.open(publicUrl, '_blank', 'noopener')}><ExternalLink className="w-4 h-4" /></Button>}
                    <Button variant="outline" size="sm" onClick={save} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save</Button>
                    {flow?.status === 'published'
                        ? <Button size="sm" variant="outline" onClick={unpublish}>Unpublish</Button>
                        : <Button size="sm" onClick={publish}><Rocket className="w-4 h-4 mr-1" /> Publish</Button>}
                </div>
            </div>

            <div className="max-w-2xl mx-auto p-6">
                {preview ? (
                    <div className="space-y-8">
                        {screens.length === 0 && <p className="text-center text-stone-400 py-10">No screens to preview.</p>}
                        {screens.map((sc, i) => (
                            <div key={sc.step_id} className="bg-white rounded-xl border border-stone-200 p-6">
                                <p className="text-xs text-stone-400 mb-1">{i + 1} / {screens.length}</p>
                                {sc.kind === 'splash'
                                    ? <SplashScreen splash={sc.splash} brandColor={branding.brand_color} buttonColor={branding.button_color} onContinue={() => {}} />
                                    : sc.kind === 'end_page'
                                    ? <EndPageRenderer endPage={sc.end_page} meta={{}} brandColor={branding.brand_color} buttonColor={branding.button_color} />
                                    : (<>
                                        <h3 className="text-lg font-semibold text-stone-900 mb-3">{sc.label || '(untitled)'} {sc.required && <span className="text-red-500">*</span>}</h3>
                                        <FlowScreen question={sc} value={undefined} onChange={() => {}} brandColor={branding.brand_color} buttonColor={branding.button_color} />
                                    </>)}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="space-y-3">
                        {screens.map((sc, i) => (
                            <div key={sc.step_id}>
                                {sc.kind === 'splash'
                                    ? <SplashCard sc={sc} i={i} total={screens.length} onPatch={(p) => patchScreen(i, p)} onMove={(d) => moveScreen(i, d)} onDelete={() => delScreen(i)} />
                                    : sc.kind === 'end_page'
                                    ? <EndPageCard sc={sc} i={i} total={screens.length} onPatch={(p) => patchScreen(i, p)} onMove={(d) => moveScreen(i, d)} onDelete={() => delScreen(i)} />
                                    : <QuestionCard q={sc} i={i} total={screens.length} customFields={customFields} onPatch={(p) => patchScreen(i, p)} onMove={(d) => moveScreen(i, d)} onDelete={() => delScreen(i)} onCreateField={() => setNewField({ index: i, label: sc.label || '' })} />}
                                <BranchEditor branch={sc.branch} priorFields={priorFields(i)} targets={laterTargets(i)} onChange={(branch) => patchScreen(i, { branch })} />
                            </div>
                        ))}
                        <div className="flex gap-2">
                            <Button variant="outline" className="flex-1" onClick={addQuestion}><Plus className="w-4 h-4 mr-1.5" /> Add question</Button>
                            <Button variant="outline" className="flex-1" onClick={addSplash}><ImageIcon className="w-4 h-4 mr-1.5" /> Add splash screen</Button>
                            <Button variant="outline" className="flex-1" onClick={addEndPage}><CheckCircle className="w-4 h-4 mr-1.5" /> Add end page</Button>
                        </div>
                    </div>
                )}
            </div>

            {/* Settings dialog */}
            <Dialog open={dialog === 'settings'} onOpenChange={() => setDialog(null)}>
                <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>Flow settings</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <label className={lbl}>Email question (for contact matching)</label>
                            <select className={inp} value={settings.email_field_id || ''} onChange={e => setSettings(s => ({ ...s, email_field_id: e.target.value || null }))}>
                                <option value="">Auto-detect (first email field)</option>
                                {screens.filter(sc => sc.kind === 'question' && sc.type === 'email').map(sc => <option key={sc.question_id} value={sc.question_id}>{sc.label || sc.question_id}</option>)}
                            </select>
                        </div>
                        <label className="flex items-center gap-2 text-sm text-stone-700">
                            <input type="checkbox" checked={settings.create_contact_if_missing !== false} onChange={e => setSettings(s => ({ ...s, create_contact_if_missing: e.target.checked }))} /> Create contact if missing
                        </label>
                        <label className="flex items-center gap-2 text-sm text-stone-700">
                            <input type="checkbox" checked={settings.capture_partial !== false} onChange={e => setSettings(s => ({ ...s, capture_partial: e.target.checked }))} /> Capture partial leads (create a lead as soon as an email is entered)
                        </label>
                        <div><label className={lbl}>Auto-apply tags (comma-separated)</label>
                            <input className={inp} value={(settings.auto_tags || []).join(', ')} onChange={e => setSettings(s => ({ ...s, auto_tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) }))} /></div>
                        <div><label className={lbl}>Confirmation message</label>
                            <input className={inp} value={settings.confirmation_message || ''} onChange={e => setSettings(s => ({ ...s, confirmation_message: e.target.value }))} placeholder="Thank you!" /></div>
                        <div><label className={lbl}>Redirect URL (optional)</label>
                            <input className={inp} value={settings.redirect_url || ''} onChange={e => setSettings(s => ({ ...s, redirect_url: e.target.value || null }))} placeholder="https://…" /></div>
                        <div className="border-t border-stone-100 pt-4">
                            <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Attribution stamped on every lead</p>
                            <div className="grid grid-cols-2 gap-3">
                                {['funnel', 'page_variant', 'form_variant', 'product_variant'].map(k => (
                                    <div key={k}><label className={lbl}>{k.replace('_', ' ')}</label>
                                        <input className={inp} value={defaults[k] || ''} onChange={e => setDefaults(d => ({ ...d, [k]: e.target.value || null }))} /></div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <DialogFooter><Button onClick={() => setDialog(null)}>Done</Button></DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Branding dialog */}
            <Dialog open={dialog === 'branding'} onOpenChange={() => setDialog(null)}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader><DialogTitle>Branding</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div><label className={lbl}>Logo URL</label><input className={inp} value={branding.logo || ''} onChange={e => setBranding(b => ({ ...b, logo: e.target.value }))} /></div>
                        {[['brand_color', 'Accent color'], ['button_color', 'Button color'], ['background', 'Background']].map(([k, label]) => (
                            <div key={k} className="flex items-center gap-3">
                                <input type="color" value={branding[k] || '#1c1917'} onChange={e => setBranding(b => ({ ...b, [k]: e.target.value }))} className="h-9 w-12 rounded border border-stone-200" />
                                <div className="flex-1"><label className={lbl}>{label}</label><input className={inp} value={branding[k] || ''} onChange={e => setBranding(b => ({ ...b, [k]: e.target.value }))} /></div>
                            </div>
                        ))}
                        <div><label className={lbl}>Font</label><input className={inp} value={branding.font || ''} onChange={e => setBranding(b => ({ ...b, font: e.target.value }))} placeholder="Manrope" /></div>
                    </div>
                    <DialogFooter><Button onClick={() => setDialog(null)}>Done</Button></DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Domain dialog */}
            <Dialog open={dialog === 'domain'} onOpenChange={() => setDialog(null)}>
                <DialogContent className="sm:max-w-md p-0 bg-transparent border-0 shadow-none">
                    <CustomDomainPanel apiBase={`${API}/form-flows/${flowId}`} getAuthHeader={getAuthHeader} onSiteUpdate={load} onClose={() => setDialog(null)} />
                </DialogContent>
            </Dialog>

            {/* Embed dialog */}
            <Dialog open={dialog === 'embed'} onOpenChange={() => setDialog(null)}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader><DialogTitle>Embed this flow</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        {flow?.status !== 'published' && <p className="text-xs text-amber-600">Publish the flow first — the embed renders the live published version.</p>}
                        <div>
                            <label className={lbl}>Public URL (embed)</label>
                            <code className="block text-xs bg-stone-50 px-3 py-2 rounded border border-stone-200 break-all">{embedSrc}</code>
                        </div>
                        <div>
                            <label className={lbl}>Auto-resize embed code (paste into any page)</label>
                            <textarea readOnly rows={6} className={`${inp} font-mono text-xs`} value={embedSnippet} onFocus={e => e.target.select()} />
                            <Button variant="outline" size="sm" className="mt-1" onClick={() => { navigator.clipboard.writeText(embedSnippet); toast.success('Embed code copied'); }}>Copy embed code</Button>
                        </div>
                    </div>
                    <DialogFooter><Button onClick={() => setDialog(null)}>Done</Button></DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Create custom field dialog */}
            <Dialog open={!!newField} onOpenChange={() => setNewField(null)}>
                <DialogContent className="sm:max-w-sm">
                    <DialogHeader><DialogTitle>Create contact field</DialogTitle></DialogHeader>
                    <div className="py-1">
                        <label className={lbl}>Field label</label>
                        <input className={inp} autoFocus value={newField?.label || ''} onChange={e => setNewField(nf => ({ ...nf, label: e.target.value }))} onKeyDown={e => e.key === 'Enter' && createField()} />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setNewField(null)}>Cancel</Button>
                        <Button onClick={createField}>Create</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function BranchEditor({ branch, priorFields, targets, onChange }) {
    const [open, setOpen] = useState(!!(branch && (branch.rules?.length || branch.default_go_to)));
    const b = branch || { rules: [], default_go_to: null };
    const hasPrior = (priorFields[0]?.fields || []).length > 0;

    const setBranch = (next) => onChange(next);
    const addRule = () => setBranch({ ...b, rules: [...(b.rules || []), { condition_group: { logic: 'and', conditions: [] }, go_to: '' }] });
    const patchRule = (idx, p) => setBranch({ ...b, rules: b.rules.map((r, i) => i === idx ? { ...r, ...p } : r) });
    const delRule = (idx) => setBranch({ ...b, rules: b.rules.filter((_, i) => i !== idx) });

    return (
        <div className="ml-3 mt-1 mb-1 border-l-2 border-stone-100 pl-3">
            <button type="button" onClick={() => setOpen(o => !o)} className="flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-700 py-1">
                <GitBranch className="w-3.5 h-3.5" /> Logic / branching {(b.rules?.length || 0) > 0 && <span className="text-stone-400">({b.rules.length})</span>}
                {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            {open && (
                <div className="space-y-3 pb-2">
                    {!hasPrior && <p className="text-[11px] text-stone-400">Add questions before this screen to branch on their answers.</p>}
                    {(b.rules || []).map((rule, idx) => (
                        <div key={idx} className="bg-stone-50 rounded-lg border border-stone-200 p-2.5 space-y-2">
                            <div className="flex items-center justify-between">
                                <span className="text-[11px] font-medium text-stone-500">If…</span>
                                <button onClick={() => delRule(idx)} className="p-0.5 text-stone-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                            </div>
                            <ConditionBuilder value={rule.condition_group} onChange={cg => patchRule(idx, { condition_group: cg })} fields={priorFields} />
                            <div className="flex items-center gap-2">
                                <span className="text-[11px] text-stone-500">→ go to</span>
                                <select className="text-xs border border-stone-200 rounded-md px-2 py-1 flex-1" value={rule.go_to || ''} onChange={e => patchRule(idx, { go_to: e.target.value })}>
                                    {targets.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                </select>
                            </div>
                        </div>
                    ))}
                    {hasPrior && <button type="button" onClick={addRule} className="text-xs text-stone-500 hover:text-stone-700 flex items-center gap-1"><Plus className="w-3 h-3" /> Add rule</button>}
                    <div className="flex items-center gap-2 pt-1">
                        <span className="text-[11px] text-stone-500">Otherwise go to</span>
                        <select className="text-xs border border-stone-200 rounded-md px-2 py-1 flex-1" value={b.default_go_to || ''} onChange={e => setBranch({ ...b, default_go_to: e.target.value || null })}>
                            {targets.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                    </div>
                </div>
            )}
        </div>
    );
}

function SplashCard({ sc, i, total, onPatch, onMove, onDelete }) {
    const sp = sc.splash || {};
    const setSp = (p) => onPatch({ splash: { ...sp, ...p } });
    const t = sp.testimonial || {};
    const setT = (p) => setSp({ testimonial: { ...t, ...p } });
    return (
        <div className="bg-white rounded-xl border border-stone-200 p-4">
            <div className="flex items-center gap-2 mb-3">
                <GripVertical className="w-4 h-4 text-stone-300" />
                <span className="text-xs font-medium text-violet-600">Splash {i + 1}</span>
                <div className="ml-auto flex items-center gap-0.5">
                    <button onClick={() => onMove(-1)} disabled={i === 0} className="p-1 text-stone-400 hover:text-stone-700 disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
                    <button onClick={() => onMove(1)} disabled={i === total - 1} className="p-1 text-stone-400 hover:text-stone-700 disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
                    <button onClick={onDelete} className="p-1 text-stone-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
            </div>
            <div className="space-y-3">
                <input className={inp} placeholder="Headline" value={sp.headline || ''} onChange={e => setSp({ headline: e.target.value })} />
                <textarea className={inp} rows={2} placeholder="Body text" value={sp.body || ''} onChange={e => setSp({ body: e.target.value })} />
                <input className={inp} placeholder="Image URL (optional)" value={sp.image_url || ''} onChange={e => setSp({ image_url: e.target.value })} />
                <div className="border-t border-stone-100 pt-3">
                    <p className="text-[11px] font-medium text-stone-500 mb-2">Testimonial (optional)</p>
                    <textarea className={inp} rows={2} placeholder="Quote" value={t.quote || ''} onChange={e => setT({ quote: e.target.value })} />
                    <div className="grid grid-cols-3 gap-2 mt-2">
                        <input className={inp} placeholder="Author" value={t.author || ''} onChange={e => setT({ author: e.target.value })} />
                        <input className={inp} placeholder="Role" value={t.role || ''} onChange={e => setT({ role: e.target.value })} />
                        <input type="number" min="0" max="5" className={inp} placeholder="Rating" value={t.rating ?? ''} onChange={e => setT({ rating: e.target.value === '' ? null : Number(e.target.value) })} />
                    </div>
                </div>
                <div className="w-40"><label className={lbl}>Button label</label><input className={inp} value={sp.button_label || ''} onChange={e => setSp({ button_label: e.target.value })} placeholder="Continue" /></div>
            </div>
        </div>
    );
}

function EndPageCard({ sc, i, total, onPatch, onMove, onDelete }) {
    const ep = sc.end_page || { headline: '', subheadline: '', blocks: [], enable_coach_assignment: false, enable_booking: false };
    const setEp = (p) => onPatch({ end_page: { ...ep, ...p } });
    const blocks = ep.blocks || [];

    const setBlocks = (next) => setEp({ blocks: next.map((b, idx) => ({ ...b, order: idx })) });
    const addBlock = (type) => setBlocks([...blocks, endBlockDefaults(type)]);
    const patchBlock = (idx, props) => setBlocks(blocks.map((b, j) => j === idx ? { ...b, props: { ...b.props, ...props } } : b));
    const toggleBlock = (idx) => setBlocks(blocks.map((b, j) => j === idx ? { ...b, enabled: b.enabled === false } : b));
    const moveBlock = (idx, d) => { const j = idx + d; if (j < 0 || j >= blocks.length) return; const n = [...blocks]; [n[idx], n[j]] = [n[j], n[idx]]; setBlocks(n); };
    const delBlock = (idx) => setBlocks(blocks.filter((_, j) => j !== idx));

    const renderBlockProps = (b, idx) => {
        const p = b.props || {};
        switch (b.type) {
            case 'headline':
            case 'subheadline':
            case 'body':
                return <textarea className={inp} rows={b.type === 'body' ? 3 : 2} placeholder="Text" value={p.text || ''} onChange={e => patchBlock(idx, { text: e.target.value })} />;
            case 'video':
                return <input className={inp} placeholder="YouTube / Vimeo / video URL" value={p.url || ''} onChange={e => patchBlock(idx, { url: e.target.value })} />;
            case 'iframe':
                return (
                    <div className="grid grid-cols-3 gap-2">
                        <input className={`${inp} col-span-2`} placeholder="Embed URL" value={p.url || ''} onChange={e => patchBlock(idx, { url: e.target.value })} />
                        <input type="number" className={inp} placeholder="Height" value={p.height ?? ''} onChange={e => patchBlock(idx, { height: e.target.value === '' ? null : Number(e.target.value) })} />
                    </div>
                );
            case 'cta':
                return (
                    <div className="space-y-2">
                        <input className={inp} placeholder="Button label" value={p.label || ''} onChange={e => patchBlock(idx, { label: e.target.value })} />
                        <input className={inp} placeholder="URL (leave blank to use funnel next)" value={p.url || ''} onChange={e => patchBlock(idx, { url: e.target.value })} />
                        <label className="flex items-center gap-2 text-xs text-stone-600">
                            <input type="checkbox" checked={!!p.use_ff_next} onChange={e => patchBlock(idx, { use_ff_next: e.target.checked })} /> Use funnel next step (ff_next)
                        </label>
                    </div>
                );
            case 'coach_card':
                return (
                    <div className="flex flex-wrap gap-3">
                        {[['show_bio', 'Show bio'], ['show_phone', 'Show phone'], ['show_linkedin', 'Show LinkedIn']].map(([k, label]) => (
                            <label key={k} className="flex items-center gap-1.5 text-xs text-stone-600">
                                <input type="checkbox" checked={p[k] !== false} onChange={e => patchBlock(idx, { [k]: e.target.checked })} /> {label}
                            </label>
                        ))}
                    </div>
                );
            case 'booking':
                return <p className="text-[11px] text-stone-400">Live calendar — leads book a time with their assigned coach (round-robin). Requires the coach's Google Calendar connected for slots + Meet links.</p>;
            default:
                return null;
        }
    };

    return (
        <div className="bg-white rounded-xl border border-stone-200 p-4">
            <div className="flex items-center gap-2 mb-3">
                <GripVertical className="w-4 h-4 text-stone-300" />
                <span className="text-xs font-medium text-emerald-600">End page {i + 1}</span>
                <div className="ml-auto flex items-center gap-0.5">
                    <button onClick={() => onMove(-1)} disabled={i === 0} className="p-1 text-stone-400 hover:text-stone-700 disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
                    <button onClick={() => onMove(1)} disabled={i === total - 1} className="p-1 text-stone-400 hover:text-stone-700 disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
                    <button onClick={onDelete} className="p-1 text-stone-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
            </div>
            <div className="space-y-3">
                <input className={inp} placeholder="Headline" value={ep.headline || ''} onChange={e => setEp({ headline: e.target.value })} />
                <textarea className={inp} rows={2} placeholder="Subheadline" value={ep.subheadline || ''} onChange={e => setEp({ subheadline: e.target.value })} />

                <div className="border-t border-stone-100 pt-3">
                    <p className="text-[11px] font-medium text-stone-500 mb-2">Blocks</p>
                    <div className="space-y-2">
                        {blocks.map((b, idx) => (
                            <div key={b.block_id} className={`rounded-lg border p-2.5 space-y-2 ${b.enabled === false ? 'border-stone-200 bg-stone-50 opacity-60' : 'border-stone-200 bg-stone-50'}`}>
                                <div className="flex items-center gap-2">
                                    <span className="text-[11px] font-medium text-stone-500">{endBlockLabel(b.type)}</span>
                                    <div className="ml-auto flex items-center gap-0.5">
                                        <button onClick={() => toggleBlock(idx)} className="text-[10px] px-1.5 py-0.5 rounded text-stone-500 hover:bg-stone-200" title="Enable / disable">{b.enabled === false ? 'Off' : 'On'}</button>
                                        <button onClick={() => moveBlock(idx, -1)} disabled={idx === 0} className="p-0.5 text-stone-400 hover:text-stone-700 disabled:opacity-30"><ChevronUp className="w-3.5 h-3.5" /></button>
                                        <button onClick={() => moveBlock(idx, 1)} disabled={idx === blocks.length - 1} className="p-0.5 text-stone-400 hover:text-stone-700 disabled:opacity-30"><ChevronDown className="w-3.5 h-3.5" /></button>
                                        <button onClick={() => delBlock(idx)} className="p-0.5 text-stone-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                                    </div>
                                </div>
                                {renderBlockProps(b, idx)}
                            </div>
                        ))}
                        {blocks.length === 0 && <p className="text-[11px] text-stone-400">No blocks yet — add one below.</p>}
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                        {END_BLOCK_TYPES.map(([v, label]) => (
                            <button key={v} type="button" onClick={() => addBlock(v)} className="text-[11px] text-stone-600 border border-stone-200 rounded-md px-2 py-1 hover:border-stone-400 hover:bg-stone-50 flex items-center gap-1">
                                <Plus className="w-3 h-3" /> {label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="border-t border-stone-100 pt-3 space-y-2">
                    <label className="flex items-center gap-2 text-xs text-stone-600">
                        <input type="checkbox" checked={!!ep.enable_coach_assignment} onChange={e => setEp({ enable_coach_assignment: e.target.checked })} /> Assign a coach to this lead
                    </label>
                    {ep.enable_coach_assignment && (
                        <div className="pl-6">
                            <label className="block text-[11px] font-medium text-stone-500 mb-1">Assignment mode</label>
                            <select className={inp} value={ep.round_robin_mode || ''} onChange={e => setEp({ round_robin_mode: e.target.value || null })}>
                                <option value="">Org default</option>
                                <option value="sequential">Sequential</option>
                                <option value="availability_based">Availability-based</option>
                            </select>
                        </div>
                    )}
                    <label className="flex items-center gap-2 text-xs text-stone-600">
                        <input type="checkbox"
                            checked={blocks.some(b => b.type === 'booking')}
                            onChange={e => {
                                const on = e.target.checked;
                                const next = on
                                    ? (blocks.some(b => b.type === 'booking') ? blocks : [...blocks, endBlockDefaults('booking')])
                                    : blocks.filter(b => b.type !== 'booking');
                                setEp({ enable_booking: on, blocks: next.map((b, idx) => ({ ...b, order: idx })) });
                            }} /> Enable booking on this page
                    </label>
                    <p className="text-[11px] text-stone-400 pl-6">Leads pick a time with their assigned coach. Connect the coach's Google Calendar (Settings) for live availability + Meet links.</p>
                </div>
            </div>
        </div>
    );
}

function QuestionCard({ q, i, total, customFields, onPatch, onMove, onDelete, onCreateField }) {
    const optionsText = (q.options || []).map(o => (typeof o === 'string' ? o : o.label)).join('\n');
    const setOptions = (text) => onPatch({ options: text.split('\n').map(l => l.trim()).filter(Boolean).map(l => ({ value: l, label: l })) });
    return (
        <div className="bg-white rounded-xl border border-stone-200 p-4">
            <div className="flex items-center gap-2 mb-3">
                <GripVertical className="w-4 h-4 text-stone-300" />
                <span className="text-xs text-stone-400">Q{i + 1}</span>
                <select className="text-sm border border-stone-200 rounded-md px-2 py-1 ml-1" value={q.type} onChange={e => onPatch({ type: e.target.value, options: HAS_OPTIONS.has(e.target.value) ? q.options : [] })}>
                    {QUESTION_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <div className="ml-auto flex items-center gap-0.5">
                    <button onClick={() => onMove(-1)} disabled={i === 0} className="p-1 text-stone-400 hover:text-stone-700 disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
                    <button onClick={() => onMove(1)} disabled={i === total - 1} className="p-1 text-stone-400 hover:text-stone-700 disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
                    <button onClick={onDelete} className="p-1 text-stone-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
            </div>
            <div className="space-y-3">
                <input className={inp} placeholder="Question label" value={q.label} onChange={e => onPatch({ label: e.target.value })} />
                <input className={inp} placeholder="Help text (optional)" value={q.help_text} onChange={e => onPatch({ help_text: e.target.value })} />
                {HAS_OPTIONS.has(q.type) && (
                    <div><label className={lbl}>Options (one per line)</label>
                        <textarea className={inp} rows={3} value={optionsText} onChange={e => setOptions(e.target.value)} /></div>
                )}
                {q.type === 'monetary' && (
                    <div className="w-32"><label className={lbl}>Currency</label>
                        <input className={inp} value={q.config?.currency || ''} placeholder="USD" onChange={e => onPatch({ config: { ...q.config, currency: e.target.value } })} /></div>
                )}
                {q.type === 'rating' && (
                    <div className="w-32"><label className={lbl}>Scale max</label>
                        <input type="number" className={inp} value={q.config?.scale_max || ''} placeholder="5" onChange={e => onPatch({ config: { ...q.config, scale_max: Number(e.target.value) || 5 } })} /></div>
                )}
                <div className="grid grid-cols-2 gap-3 items-end">
                    <div>
                        <label className={lbl}>Map to CRM field</label>
                        <select className={inp} value={q.map_to || ''} onChange={e => { if (e.target.value === '__create__') { onCreateField(); return; } onPatch({ map_to: e.target.value || null }); }}>
                            <option value="">— none —</option>
                            <optgroup label="Contact">{CONTACT_FIELDS.map(f => <option key={f} value={f}>{f}</option>)}</optgroup>
                            <optgroup label="Company">{ACCOUNT_FIELDS.map(f => <option key={f} value={f}>{f}</option>)}</optgroup>
                            {customFields.length > 0 && <optgroup label="Custom">{customFields.map(cf => <option key={cf.key} value={cf.key}>{cf.label}</option>)}</optgroup>}
                            <option value="__create__">+ Create new field…</option>
                        </select>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-stone-700 pb-2">
                        <input type="checkbox" checked={q.required} onChange={e => onPatch({ required: e.target.checked })} /> Required
                    </label>
                </div>
            </div>
        </div>
    );
}
